import { useCallback, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { ArrowDown, SWReactCountryIcons, Worldwide } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { LocationOption } from "../types";

import { DropdownTrigger } from "./styles";

type Props = {
    selectedLocationIds: Set<string>;
    locationOptions: LocationOption[];
    onLocationsChange: (ids: Set<string>) => void;
};

export const LocationPicker = ({ selectedLocationIds, locationOptions, onLocationsChange }: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [tempLocationIds, setTempLocationIds] = useState<Set<string>>(selectedLocationIds);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                setTempLocationIds(new Set(selectedLocationIds));
            }
            setIsOpen(open);
        },
        [selectedLocationIds],
    );

    const handleToggle = useCallback((locationId: string) => {
        setTempLocationIds((prev) => {
            const next = new Set(prev);
            if (next.has(locationId)) {
                next.delete(locationId);
            } else {
                next.add(locationId);
            }
            return next;
        });
    }, []);

    const handleToggleAll = useCallback(() => {
        setTempLocationIds((prev) => {
            const allSelected = prev.size === locationOptions.length;
            if (allSelected) {
                return new Set<string>();
            }
            return new Set(locationOptions.map((loc) => loc.id));
        });
    }, [locationOptions]);

    const handleApply = useCallback(() => {
        onLocationsChange(tempLocationIds);
        setIsOpen(false);
    }, [onLocationsChange, tempLocationIds]);

    const selectedLabel =
        selectedLocationIds.size === locationOptions.length
            ? translate("rankTracker.addKeywords.modal.allLocations")
            : translate("rankTracker.addKeywords.modal.locationCount", { count: selectedLocationIds.size });

    const allLocationsSelected = tempLocationIds.size === locationOptions.length;

    return (
        <Combobox
            isOpen={isOpen}
            data-automation="add-keywords-modal-location-picker"
            onOpenChange={handleOpenChange}
        >
            <Combobox.Trigger>
                <DropdownTrigger data-automation="add-keywords-modal-location-picker-trigger">
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <Worldwide
                            size="xs"
                            color={colorsPalettes.carbon[200]}
                        />
                        <Text fontVariant="value">{selectedLabel}</Text>
                    </Flex>
                    <ArrowDown size="xs" />
                </DropdownTrigger>
            </Combobox.Trigger>

            <Combobox.Content data-automation="add-keywords-modal-location-picker-content">
                <Combobox.Option
                    key="all-locations"
                    data-automation="add-keywords-modal-location-picker-select-all"
                    onClick={handleToggleAll}
                >
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Combobox.OptionLabel>
                            {translate("rankTracker.addKeywords.modal.allLocations")}
                        </Combobox.OptionLabel>
                        <Combobox.OptionCheckbox isChecked={allLocationsSelected} />
                    </Flex>
                </Combobox.Option>

                <Combobox.Separator />

                {locationOptions.map((location) => (
                    <Combobox.Option
                        key={location.id}
                        data-automation={`add-keywords-modal-location-picker-option-${location.id}`}
                        onClick={() => handleToggle(location.id)}
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                <SWReactCountryIcons
                                    countryCode={location.countryIconID}
                                    size="16px"
                                />
                                <Combobox.OptionLabel>{location.text}</Combobox.OptionLabel>
                            </Flex>
                            <Combobox.OptionCheckbox isChecked={tempLocationIds.has(location.id)} />
                        </Flex>
                    </Combobox.Option>
                ))}
            </Combobox.Content>

            <Combobox.ApplyButton
                data-automation="add-keywords-modal-location-picker-apply"
                isDisabled={tempLocationIds.size === 0}
                onClick={handleApply}
            >
                {translate("common.apply")}
            </Combobox.ApplyButton>
        </Combobox>
    );
};
